import {
  CPT,
  Diagnosis,
  ICD,
  OrderTargetProvider,
  PaginationInput,
  Provider,
  Specialty,
  LoincCode,
} from '@getvim-os/types';
import { AppEhrState, OSEhrState, OSEhrStateWithPII } from '@getvim/vim-os-api';
import { EHR } from '@getvim/vim-os-sdk-api';
import { EhrResourceEnhancementsExecutor } from '../ehrResourceEnhancements';
import { OSToAppEhrConvertor } from './interface';

const ICD_SYSTEM = 'supported ICD-10';
const CPT_SYSTEM = 'supported CPT-4';

type EHR_STATE = EHR.AppEhrState<{ withPII: true }>;

const pickSpeicalityDescription = (specialty: Specialty[]) =>
  specialty.reduce<string[]>((acc, s) => (s.description ? acc.concat(s.description) : acc), []);

const convertToDiagnoses = <T extends { code?: string }>(
  items: T[],
  mapper: (item: T) => Omit<EHR.Diagnosis, 'system'>,
): EHR.Diagnosis[] =>
  items.reduce<EHR.Diagnosis[]>(
    (acc, item) =>
      item.code
        ? acc.concat({
            ...mapper(item),
            system: ICD_SYSTEM,
          })
        : acc,
    [],
  );

const convertIcds = (icds: ICD[]): EHR.Diagnosis[] =>
  convertToDiagnoses(icds, (d) => ({
    code: d.code!,
    description: d.name,
  }));

export const convertDiagnoses = (diagnosis: Diagnosis[]): EHR.Diagnosis[] =>
  convertToDiagnoses(diagnosis, (d) => ({
    code: d.code,
    description: d.description,
  }));

export const convertEncounterDiagnoses = (diagnosis: Diagnosis[]): EHR.Diagnosis[] =>
  convertToDiagnoses(diagnosis, (d) => ({
    code: d.code,
    description: d.description,
    note: d.note,
  }));

const convertCpts = (cpts: CPT[]): EHR.ProceduresCodes[] =>
  cpts.reduce<EHR.ProceduresCodes[]>(
    (acc, cpt) =>
      cpt.code
        ? acc.concat({
            code: cpt.code,
            description: cpt.name,
            system: CPT_SYSTEM,
          })
        : acc,
    [],
  );

const convertLoincCodes = (loincCodes: LoincCode[]): EHR.Order['loincCodes'] => {
  return loincCodes.map((l) => ({ code: l.code, description: l.description }));
};

const convertOrganization = (
  organization: NonNullable<Provider['organization']>,
): EHR.ProviderFacility => {
  const { ehrId, name, address, contact_info, npi } = organization;
  return {
    facilityEhrId: ehrId,
    name,
    address,
    contact_info,
    facilityNpi: npi,
  };
};

function convertProvider(provider: Provider): EHR.Provider;
function convertProvider(provider: OrderTargetProvider): EHR.OrderTargetProvider;
function convertProvider<T extends Provider | OrderTargetProvider>(
  provider: T,
): EHR.Provider | EHR.OrderTargetProvider {
  const { providerEhrId, npi, demographics, organization, specialty, degree, type } = provider;

  const appProvider = {
    ehrProviderId: providerEhrId,
    npi,
    demographics,
    facility: organization && convertOrganization(organization),
    providerDegree: degree,
    type: type,
  } as EHR.Provider | EHR.OrderTargetProvider;
  if (specialty) {
    appProvider.specialty = pickSpeicalityDescription(specialty);
  }
  return appProvider;
}

const convertMedications = (medications: EHR.Order['medications']): EHR.Order['medications'] => {
  if (!medications?.length) {
    return medications;
  }

  return medications.map(({ basicInformation, dosage }) => ({ basicInformation, dosage }));
};

const convertServiceLines = (serviceLines) => {
  if (!serviceLines?.length) {
    return serviceLines;
  }
  return serviceLines.map((serviceLine) => {
    const { procedureCode, procedureDescription, proceduresModifiers, units, diagnoses } =
      serviceLine;
    return {
      procedureCode,
      procedureSystem: CPT_SYSTEM,
      diagnoses: diagnoses && convertDiagnoses(diagnoses),
      procedureDescription,
      proceduresModifiers,
      units,
    };
  });
};

export class OsToExternalAppEhrConvertor implements OSToAppEhrConvertor<EHR_STATE> {
  getProblemList = async (
    vimPatientId: string | undefined,
    ehrResourceEnhancementsExecutor: EhrResourceEnhancementsExecutor<EHR_STATE>,
  ) => {
    if (!vimPatientId) {
      throw new Error('vimPatientId is undefined! unable to call getProblemList');
    }
    const problemList = await ehrResourceEnhancementsExecutor.getProblemList(vimPatientId);
    return problemList?.map((problem) => {
      return {
        ...problem,
        system: ICD_SYSTEM,
      };
    });
  };

  getMedicationList = async (
    vimPatientId: string | undefined,
    ehrResourceEnhancementsExecutor: EhrResourceEnhancementsExecutor<EHR_STATE>,
  ) => {
    if (!vimPatientId) {
      throw new Error('vimPatientId is undefined! unable to call getMedicationList');
    }
    return await ehrResourceEnhancementsExecutor.getMedicationList(vimPatientId);
  };

  getAllergyList = async (
    vimPatientId: string | undefined,
    ehrResourceEnhancementsExecutor: EhrResourceEnhancementsExecutor<EHR_STATE>,
  ) => {
    if (!vimPatientId) {
      throw new Error('vimPatientId is undefined! unable to call getAllergyList');
    }
    return await ehrResourceEnhancementsExecutor.getAllergyList(vimPatientId);
  };

  getLabResults = async (
    vimPatientId: string | undefined,
    ehrResourceEnhancementsExecutor: EhrResourceEnhancementsExecutor<EHR_STATE>,
    input: PaginationInput,
  ) => {
    if (!vimPatientId) {
      throw new Error('vimPatientId is undefined! unable to call getLabResults');
    }
    const labResults = await ehrResourceEnhancementsExecutor.getLabResults(input, vimPatientId);
    return labResults
      ? {
          ...labResults,
          items: labResults.items?.map((item) => ({
            ...item,
            basicInformation: {
              ...item.basicInformation,
              system: ICD_SYSTEM,
            },
          })),
        }
      : undefined;
  };

  getVitals = async (
    vimPatientId: string | undefined,
    ehrResourceEnhancementsExecutor: EhrResourceEnhancementsExecutor<EHR_STATE>,
    input: PaginationInput,
  ) => {
    if (!vimPatientId) {
      throw new Error('vimPatientId is undefined! unable to call getVitals');
    }
    return await ehrResourceEnhancementsExecutor.getVitals(input, vimPatientId);
  };

  convertPatientWithPII = (
    osPatient: NonNullable<OSEhrStateWithPII['patient']>,
    ehrResourceEnhancementsExecutor: EhrResourceEnhancementsExecutor<EHR_STATE>,
  ): EHR.Patient => {
    const { patientId, vimPatientId, mrn, demographics, insurance, pcp, contact_info } = osPatient;
    const { address, ...otherDemographics } = demographics || { address: undefined };

    const appPatient: EHR.Patient = {
      identifiers: {
        ehrPatientId: patientId,
        vimPatientId,
        mrn,
      },
      contact_info,
      demographics: otherDemographics,
      address,
      getProblemList: () =>
        this.getProblemList(appPatient.identifiers?.vimPatientId, ehrResourceEnhancementsExecutor),
      getMedicationList: () =>
        this.getMedicationList(
          appPatient.identifiers?.vimPatientId,
          ehrResourceEnhancementsExecutor,
        ),
      getAllergyList: () =>
        this.getAllergyList(appPatient.identifiers?.vimPatientId, ehrResourceEnhancementsExecutor),
      getLabResults: (input?: PaginationInput) =>
        this.getLabResults(
          appPatient.identifiers?.vimPatientId,
          ehrResourceEnhancementsExecutor,
          input || { page: 1 },
        ),
      getVitals: (input?: PaginationInput) =>
        this.getVitals(
          appPatient.identifiers?.vimPatientId,
          ehrResourceEnhancementsExecutor,
          input || { page: 1 },
        ),
    };

    if (insurance) {
      const { ehrInsurance, groupId, payerId, memberId } = insurance;
      appPatient.insurance = {
        ehrInsurance,
        groupId,
        payerId,
        memberId,
      };
    }
    if (pcp) {
      appPatient.pcp = convertProvider(pcp);
    }

    return appPatient;
  };

  convertReferralWithPII = (
    osReferral: NonNullable<OSEhrStateWithPII['referral']>,
  ): NonNullable<EHR.Referral> => {
    const {
      referralId,
      vimReferralId,
      specialty,
      diagnosis,
      targetProvider,
      referringProvider,
      startDate,
      endDate,
      createdDate,
      status,
      priority,
      numberOfVisits,
      cpts,
      facilityName,
      authCode,
      isLocked,
      reasons,
      notes,
    } = osReferral;
    return {
      identifiers: {
        ehrReferralId: referralId,
        vimReferralId,
      },
      basicInformation: {
        specialty: specialty?.description,
        startDate,
        endDate,
        createdDate,
        status,
        priority,
        numberOfVisits: numberOfVisits ? Number(numberOfVisits) : undefined,
        facilityName,
        authCode,
        isLocked,
        reasons,
        notes,
      },
      targetProvider: targetProvider && convertProvider(targetProvider),
      referringProvider: referringProvider && convertProvider(referringProvider),
      conditions: {
        diagnosis: diagnosis && convertDiagnoses(diagnosis),
      },
      procedureCodes: {
        cpts: cpts && convertCpts(cpts),
      },
    };
  };

  convertEncounter = (osEncounter: NonNullable<OSEhrStateWithPII['encounter']>): EHR.Encounter => {
    const {
      encounterId,
      encounterDate,
      provider,
      isLocked,
      soapAssessments,
      subjective,
      objective,
      plan,
      patientInstructions,
      encounterNotes,
      basicInformation,
    } = osEncounter;
    return {
      identifiers: { ehrEncounterId: encounterId },
      provider: provider && convertProvider(provider),
      assessment: {
        generalNotes: soapAssessments?.generalNotes,
        diagnosisCodes:
          soapAssessments?.diagnosisCodes &&
          convertEncounterDiagnoses(soapAssessments.diagnosisCodes),
      },
      subjective: {
        chiefComplaintNotes: subjective?.chiefComplaintNotes,
        historyOfPresentIllnessNotes: subjective?.historyOfPresentIllnessNotes,
        reviewOfSystemsNotes: subjective?.reviewOfSystemsNotes,
        generalNotes: subjective?.generalNotes,
        medicalHistoryNotes: subjective?.medicalHistoryNotes,
        surgicalHistoryNotes: subjective?.surgicalHistoryNotes,
        hospitalizationNotes: subjective?.hospitalizationNotes,
        familyHistoryNotes: subjective?.familyHistoryNotes,
        socialHistoryNotes: subjective?.socialHistoryNotes,
      },
      objective: {
        generalNotes: objective?.generalNotes,
        physicalExamNotes: objective?.physicalExamNotes,
      },
      basicInformation: {
        status: isLocked === true ? 'LOCKED' : isLocked === false ? 'UNLOCKED' : undefined,
        encounterDateOfService: encounterDate,
        type: basicInformation?.type,
        selfPay: basicInformation?.selfPay,
      },
      plan: {
        generalNotes: plan?.generalNotes,
        procedureCodes: plan?.procedureCodes && convertCpts(plan.procedureCodes),
        nextAppointmentFollowUpNotes: plan?.nextAppointmentFollowUpNotes,
      },
      patientInstructions: {
        generalNotes: patientInstructions?.generalNotes,
      },
      encounterNotes: {
        generalNotes: encounterNotes?.generalNotes,
      },
    };
  };

  convertOrders = (osOrders: NonNullable<OSEhrState['orders']>): EHR.Order[] => {
    return osOrders.map<EHR.Order>((order) => {
      const {
        id,
        encounterId,
        cpts,
        icd,
        createdDate,
        type,
        medications,
        orderingProvider,
        targetProvider,
        basicInformation,
        loincCodes,
      } = order;
      return {
        identifiers: {
          ehrOrderId: id,
        },
        basicInformation: {
          type,
          ehrEncounterId: encounterId,
          createdDate,
          notes: basicInformation?.notes,
          orderName: basicInformation?.orderName,
          reason: basicInformation?.reason,
        },
        assessments: {
          assessments: icd && convertIcds(icd),
        },
        procedureCodes: {
          procedureCodes: cpts && convertCpts(cpts),
        },
        medications: convertMedications(medications),
        orderingProvider: orderingProvider && convertProvider(orderingProvider),
        targetProvider: targetProvider && convertProvider(targetProvider),
        loincCodes: loincCodes && convertLoincCodes(loincCodes),
      };
    });
  };

  convertClaim = (claim: NonNullable<OSEhrState['claim']>): EHR.Claim => {
    const { identifiers, basicInformation, renderingProvider, serviceLines, additionalDiagnoses } =
      claim;
    return {
      identifiers,
      basicInformation,
      renderingProvider: renderingProvider && convertProvider(renderingProvider),
      serviceLines: convertServiceLines(serviceLines),
      additionalDiagnoses: additionalDiagnoses && convertDiagnoses(additionalDiagnoses),
    };
  };

  constructor() {}
  convertEhr: OSToAppEhrConvertor<EHR_STATE>['convertEhr'] = (
    { patient, encounter, referral, orders, claim },
    ehrResourceEnhancementsExecutor,
  ) => {
    return {
      patient: patient && this.convertPatientWithPII(patient, ehrResourceEnhancementsExecutor),
      referral: referral && this.convertReferralWithPII(referral),
      encounter: encounter && this.convertEncounter(encounter),
      orders: orders && this.convertOrders(orders),
      claim: claim && this.convertClaim(claim),
    };
  };

  convertResource: OSToAppEhrConvertor<EHR_STATE>['convertResource'] = (
    resource,
    value,
    ehrResourceEnhancementsExecutor,
  ) => {
    const converters: {
      [K in EHR.EHRResource]: (
        v: NonNullable<AppEhrState<{ withPII: true }>[K]>,
      ) => NonNullable<EHR_STATE[K]>;
    } = {
      [EHR.EHRResource.patient]: (v) =>
        this.convertPatientWithPII(v, ehrResourceEnhancementsExecutor),
      [EHR.EHRResource.referral]: (v) => this.convertReferralWithPII(v),
      [EHR.EHRResource.encounter]: (v) => this.convertEncounter(v),
      [EHR.EHRResource.orders]: (v) => this.convertOrders(v),
      [EHR.EHRResource.claim]: (v) => this.convertClaim(v),
      [EHR.EHRResource.customEhrData]: (v) => v,
    };
    return converters[resource](value);
  };
}
